import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import { TranslateService } from '@ngx-translate/core';

import { SkillSpecializationListItem } from '../../../models/skill-learning.model';
import { SkillSpecializationFacadeService } from '../../../services/skill-specialization-facade.service';
import { SkillRoleService } from '../../../services/skill-role.service';
import { ToastService } from '../../../../../../services/toast.service';
import { SidebarService } from '../../../../../../services/side-bar.service';

const ROUTE_SPECIALIZATIONS = '/dashboard/specializations';
const ROUTE_CREATE = '/dashboard/specializations/create';
const ROUTE_EDIT_PREFIX = '/dashboard/specializations';

@Component({
  selector: 'app-skill-specialization-list-container',
  templateUrl: './specialization-list-container.component.html',
  styleUrls: ['./specialization-list-container.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillSpecializationListContainerComponent implements OnInit, OnDestroy {

  specializations: SkillSpecializationListItem[] = [];
  pendingDeleteId: string | null = null;
  isLoading: boolean = false;
  hasLoadError: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private facade: SkillSpecializationFacadeService,
    private roleService: SkillRoleService,
    private toastService: ToastService,
    private sidebarService: SidebarService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
  ) {}

  get canManage(): boolean {
    return this.roleService.isHeadOrAdmin();
  }

  get shouldShowNoAssignedSpecializations(): boolean {
    if (this.canManage || this.hasLoadError) {
      return false;
    }
    return this.roleService.isSpecializationAdmin() && this.specializations.length === 0;
  }

  get pendingDeleteName(): string {
    if (!this.pendingDeleteId) {
      return '';
    }
    const found = this.specializations.find(s => s.id === this.pendingDeleteId);
    return found ? found.name : '';
  }

  ngOnInit(): void {
    this.sidebarService.setSelectedMenuOption('spec-list');
    this.loadSpecializations();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onEditRequested(id: string): void {
    this.router.navigate([ROUTE_EDIT_PREFIX, id, 'edit', 'general']);
  }

  onDeleteRequested(id: string): void {
    this.pendingDeleteId = id;
    this.cdr.markForCheck();
  }

  onCreateRequested(): void {
    this.router.navigate([ROUTE_CREATE]);
  }

  confirmDelete(): void {
    if (!this.pendingDeleteId) {
      return;
    }
    this.facade.deleteSpecialization(this.pendingDeleteId)
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.handleDeleteSuccess(),
        () => this.handleDeleteError(),
      );
  }

  cancelDelete(): void {
    this.pendingDeleteId = null;
    this.cdr.markForCheck();
  }

  private loadSpecializations(): void {
    this.isLoading = true;
    this.hasLoadError = false;
    this.facade.fetchSpecializationList()
      .takeUntil(this.destroy$)
      .subscribe(
        list => this.handleLoadSuccess(list),
        () => this.handleLoadError(),
      );
  }

  private handleLoadSuccess(list: SkillSpecializationListItem[]): void {
    this.specializations = list;
    this.isLoading = false;
    this.hasLoadError = false;
    this.cdr.markForCheck();
  }

  private handleLoadError(): void {
    this.isLoading = false;
    this.hasLoadError = true;
    this.toastService.error(this.translate.instant('SKILL_LEARNING_ADMIN_LIST_LOAD_ERROR'));
    this.cdr.markForCheck();
  }

  private handleDeleteSuccess(): void {
    this.specializations = this.specializations.filter(s => s.id !== this.pendingDeleteId);
    this.pendingDeleteId = null;
    this.toastService.success(this.translate.instant('SKILL_LEARNING_ADMIN_LIST_DELETE_SUCCESS'));
    this.cdr.markForCheck();
  }

  private handleDeleteError(): void {
    this.pendingDeleteId = null;
    this.toastService.error(this.translate.instant('SKILL_LEARNING_ADMIN_LIST_DELETE_ERROR'));
    this.cdr.markForCheck();
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/specialization-list-container/specialization-list-container.component.ts